import React, { useState, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { Tooltip, Spin, Button } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleXmark } from '@fortawesome/free-solid-svg-icons';
import { es } from 'date-fns/locale';
import { IHotel, IGroupedShareCompareHotel, IShareCompareHotel, LocationsTypes, ISessionKey } from '@share/common-types';
import { RefundableEnum } from '@common-types';
import { IMarginatorState, ILoginState, hotelsActions, marginatorActions, datesActions, roomsActions, getHotels, locationActions } from '@share/store/slices';
import { formatDateCheckInOut, renderNumber, UrlUtils, LoginType, isSpanish, getAccountNumberPath, VACATIONS_PAGE } from '@share/utils';
import { hotelDetailsActions, roomsSearchActions } from '@store/slices';
import { AVAILABILITY_SOLDOUT } from '@share/constants';
import { getAmenityImage, HasPermission, ViewQuotesSharePermission, } from '@utils';
import { Currency, HotelStars } from '@components';
import { ModalConfirmation } from '@components';
import { BlueButton } from '@share/components';
import { Routes } from '@share/constants';
import { DATE_FORMAT_CHECK_IN_OUT, DATES_LABEL, LOCATION_LABEL, LOCATION_OBJECT_LABEL, NULL_VALUE, ROOMS_LABEL, SESSION_KEY_LABEL } from '@constants';
import NoImage from '@assets/images/amenities/no-picture.png';
import { getModalCompareMealPlanBadgeKind, MODAL_COMPARE_MEAL_PLAN_BADGE_COLORS } from '../../../../result/result-wrapper/modal-compare/meal-plan-badge-theme';
import { useHistory } from 'react-router-dom';
import { EnvironmentOutlined, CalendarOutlined } from "@ant-design/icons";

interface IHotelTabProps {
  hotels: Record<string, IGroupedShareCompareHotel[]>;
  marginatorStore: IMarginatorState;
  loginStore: ILoginState;
  isVacationRentals?: boolean | null | undefined;
  sessionKey: ISessionKey | undefined;
  onHotelRemove: (hotel: IShareCompareHotel) => void;
  onHotelGroupRemove: (hotels: IShareCompareHotel[]) => void;
  onCancel?: () => void;
  handelDownload: () => void;
  handelShare: () => void;
}

const percents = 100;

export const HotelTab: React.FC<IHotelTabProps> = ({
  hotels,
  marginatorStore,
  loginStore,
  isVacationRentals,
  sessionKey,
  onHotelRemove,
  onHotelGroupRemove,
  onCancel,
  handelDownload,
  handelShare,
}) => {
  const [selectedLocation, setSelectedLocation] = useState(Object.keys(hotels)[0] || '');
  const [activeGroupIndex, setActiveGroupIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showRedirectConfirmation, setShowRedirectConfirmation] = useState(false);
  const [pendingHotel, setPendingHotel] = useState<IShareCompareHotel | null>(null);
  const dispatch = useDispatch();
  const history = useHistory();

  const locations = Object.keys(hotels);
  const currentGroups = hotels[selectedLocation] || [];

  useEffect(() => {
    if (!selectedLocation && locations.length > 0) {
      setSelectedLocation(locations[0]);
    } else if (selectedLocation && !hotels[selectedLocation]) {
      setSelectedLocation(locations[0] || '');
    }
  }, [hotels]);

  useEffect(() => {
    if (activeGroupIndex >= currentGroups.length && currentGroups.length > 0) {
      setActiveGroupIndex(currentGroups.length - 1);
    }
  }, [currentGroups.length]);

  const { account, user } = loginStore;
  const isB2C = account?.isB2C;
  const compareRowHasMealPlan = currentGroups[activeGroupIndex]?.items.some((h) => !isEmpty(h.mealPLan?.trim()));

  const getSearchParamsFromHotel = (hotel: IShareCompareHotel) => {
    const fallbackLocation = {
      code: hotel.hotelId,
      name: `${hotel.displayName || ''}${hotel.city ? `, ${hotel.city}` : ''}${hotel.state ? `, ${hotel.state}` : ''}${hotel.countryCode ? `, ${hotel.countryCode}` : ''}`,
      type: LocationsTypes.Hotel,
      realType: LocationsTypes.Hotel,
      geoLocation: hotel.location,
    };
    const selectedLocationObj = (hotel as any)?.locationObj || fallbackLocation;
    const dates = { startDate: hotel.startDate, endDate: hotel.endDate };
    const rooms = hotel.rooms?.map((r) => ({ adultsCount: r.adultsCount, kids: r.kids?.map((k) => k.age) || [] }));

    return {
      selectedLocationObj,
      dates,
      rooms,
    };
  };

  const handleView = (hotel: IHotel, marginatorCommission: number) => {
    const { hotelId, availability } = hotel;
    dispatch(marginatorActions.setCommission(marginatorCommission));
    dispatch(marginatorActions.setSupplier(hotel.supplierId));
    if (availability !== AVAILABILITY_SOLDOUT) {
      dispatch(hotelDetailsActions.setHotelDetails(NULL_VALUE));
      dispatch(roomsSearchActions.resetState());
      dispatch(hotelsActions.setSelectedHotel(hotel));

      const { selectedLocationObj, dates, rooms } = getSearchParamsFromHotel(hotel as IShareCompareHotel);

      const params = [
        { key: DATES_LABEL, value: dates },
        { key: ROOMS_LABEL, value: rooms },
        { key: LOCATION_OBJECT_LABEL, value: selectedLocationObj },
        { key: LOCATION_LABEL, value: selectedLocationObj.name },
        { key: SESSION_KEY_LABEL, value: sessionKey },
      ];

      const queryString = UrlUtils.getQueryString(params);
      const accountPath = getAccountNumberPath(history);
      const baseUrl = `${accountPath}${Routes.Hotel}/${hotelId}${isVacationRentals ? `/${VACATIONS_PAGE}` : ""}`;
      const Url = `${baseUrl}?${queryString}`;
      window.open(Url, "_blank");
    }
  };

  const onHotelSearchPage = (hotel: IShareCompareHotel) => {
    const fallbackLocation = {
      code: hotel.hotelId,
      name: `${hotel.displayName || ''}${hotel.city ? `, ${hotel.city}` : ''}${hotel.state ? `, ${hotel.state}` : ''}${hotel.countryCode ? `, ${hotel.countryCode}` : ''}`,
      type: LocationsTypes.Hotel,
      realType: LocationsTypes.Hotel,
      geoLocation: hotel.location,
    };
    const selectedLocationObj = (hotel as any)?.locationObj || fallbackLocation;

    UrlUtils.setUrl(LOCATION_OBJECT_LABEL, selectedLocationObj);
    UrlUtils.setUrl(LOCATION_LABEL, selectedLocationObj.name);
    UrlUtils.setUrl(DATES_LABEL, { startDate: hotel.startDate, endDate: hotel.endDate });
    UrlUtils.setUrl(ROOMS_LABEL, hotel.rooms?.map((r) => ({ adultsCount: r.adultsCount, kids: r.kids?.map((k) => k.age) || [] })));

    const currentPath = history.location.pathname.toLowerCase();
    const isHotelPage = currentPath.includes('/search') && !currentPath.includes('/cars') && !currentPath.includes('/condo') && !currentPath.includes('/vacation');
    onCancel?.();
    if (!isHotelPage) {
      const accountName = account?.name || '';
      history.push(`/${accountName}${Routes.Search}${window.location.search}`);
    } else {
      dispatch(locationActions.setSelectLocation(selectedLocationObj as any));
      dispatch(locationActions.setSelectLocationLabel(selectedLocationObj.name));
      dispatch(datesActions.setDatesSelected({ startDate: hotel.startDate, endDate: hotel.endDate }));
      if (hotel.rooms?.length) {
        dispatch(roomsActions.setRooms(
          hotel.rooms.map((r) => ({
            id: (r as any).id || `room-${Math.random()}`,
            adultsCount: r.adultsCount,
            kids: r.kids || [],
          }))
        ));
      }
      dispatch(getHotels() as any);
    }
  };

  const hotel = currentGroups[activeGroupIndex]?.items[0];
  const locationName = hotel?.locationObj?.name || selectedLocation;
  const adultsCountMeta = hotel?.rooms?.reduce((acc, room) => acc + (room.adultsCount || 0), 0) || 0;
  const kidsCountMeta = hotel?.rooms?.reduce((acc, room) => acc + (room.kids?.length || 0), 0) || 0;
  const redirectMessage = 'You will redirect to search page with this specific date and detail. Are you sure?';

  const onClickMeta = () => {
    if (!hotel) return;
    setPendingHotel(hotel);
    setShowRedirectConfirmation(true);
  };

  const onConfirmRedirect = () => {
    if (pendingHotel) {
      onHotelSearchPage(pendingHotel);
    }
    setShowRedirectConfirmation(false);
    setPendingHotel(null);
  };

  const onCancelRedirect = () => {
    setShowRedirectConfirmation(false);
    setPendingHotel(null);
  };

  return (
    <div>
 {/* LOCATION TABS */}
      {(locations.length > 1 || currentGroups.length > 1) && (
        <div className="hotels-wrapper">
          {/* LOCATION TABS */}
          {locations.length > 1 && (
            <div className="hotels-row">
              <div className="icon-box">
                <EnvironmentOutlined />
              </div>

              <div className="tabs-container locations-tabs">
                {locations.map((locCode) => (
                  <button
                    key={locCode}
                    className={`tab-btn ${
                      selectedLocation === locCode ? "active" : ""
                    }`}
                    onClick={() => {
                      setSelectedLocation(locCode);
                      setActiveGroupIndex(0);
                    }}
                  >
                    {hotels[locCode][0]?.items[0]?.locationObj?.name || locCode}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* DATE + GUEST TABS */}
          {currentGroups.length > 1 && (
            <div className="hotels-row">
              <div className="icon-box">
                <CalendarOutlined />
              </div>

              <div className="tabs-container">
                {currentGroups.map((group, index) => {
                  const { adults, kids } = group.guest;

                  return (
                    <button
                      key={index}
                      className={`tab-btn ${
                        activeGroupIndex === index ? "active" : ""
                      }`}
                      onClick={() => setActiveGroupIndex(index)}
                    >
                      {formatDateCheckInOut(
                        group.startDate,
                        DATE_FORMAT_CHECK_IN_OUT
                      )}{" "}
                      -{" "}
                      {formatDateCheckInOut(
                        group.endDate,
                        DATE_FORMAT_CHECK_IN_OUT
                      )}
                      {" - "}
                      {adults}{" "}
                      <FormattedMessage id="adult" values={{ count: adults }} />
                      {kids > 0 && (
                        <>
                          {" - "}
                          {kids}{" "}
                          <FormattedMessage id="child" values={{ count: kids }} />
                        </>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      <div className='modal-compare__hotels-location'>
        {locationName ? <div>{locationName}</div> : null}
      </div>

      <div className="modal-compare__hotels modal-compare__hotels--meta" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <a className='modal-compare__hotels_link' onClick={onClickMeta}>
          {hotel && (
            <>
              <div className="hotels-dates">
                {formatDateCheckInOut(hotel.startDate, DATE_FORMAT_CHECK_IN_OUT, isSpanish() ? es : undefined)} -{' '}
                {formatDateCheckInOut(hotel.endDate, DATE_FORMAT_CHECK_IN_OUT, isSpanish() ? es : undefined)}
              </div>
              <div className='modal-compare__hotels_link_separator'>-</div>
              <div className="hotels-dates">
                {hotel.rooms?.length} <FormattedMessage id="room" values={{ count: hotel.rooms?.length }} /> - {adultsCountMeta}{' '}
                <FormattedMessage id="adult" values={{ count: adultsCountMeta }} /> - {kidsCountMeta}{' '}
                <FormattedMessage id="child" values={{ count: kidsCountMeta }} />
              </div>
            </>
          )}
        </a>
        {(locations.length > 1 || currentGroups.length > 1) && (
          <Button
            danger
            onClick={() => onHotelGroupRemove(currentGroups[activeGroupIndex]?.items)}
          >
            <FormattedMessage id="results.hotel.card.compare.modal.remove_quote" defaultMessage="Remove Quote" />
          </Button>
        )}
      </div>

      <div className="modal-compare__hotels">
        {currentGroups[activeGroupIndex]?.items.map((hotelItem, index) => {
          const { price } = hotelItem;

          const value = marginatorStore.value || 0;
          const marginatorPrice = price / (1 - value / 100);
          const marginatorCommission = marginatorPrice - price;

          const publicPrice =
            isB2C || !hotelItem.publicPrices?.length ? null : Math.min(...hotelItem.publicPrices.map((p) => p.price));

          const msave = publicPrice ? (marginatorPrice * percents) / publicPrice : 100;
          const savings = Math.floor(percents - msave);

          return (
            <div key={index} className="modal-compare__hotel">
              <div className="modal-compare__media">
                <div className="hotel-images-container">
                  <div className="hotel-image" style={{ backgroundImage: `url(${get(hotelItem, 'images[0].url')})` }} />
                  {savings > 0 ? (
                    <div className="modal-compare__savings-badge">
                      {savings.toFixed(0)} % <FormattedMessage id="results.hotel.card.compare.modal.off" />
                    </div>
                  ) : null}
                </div>
                <label
                  className={`modal-compare__remove-btn ${currentGroups[activeGroupIndex]?.items.length <= 2 ? 'disabled' : ''}`}
                  onClick={() => {
                    if (currentGroups[activeGroupIndex]?.items.length > 2) {
                      onHotelRemove(hotelItem);
                    }
                  }}
                >
                  <FontAwesomeIcon icon={faCircleXmark} className="modal-compare__remove-btn-icon" />
                </label>
              </div>

              <div
                className={`hotel-text-container hotel-text-container--title${compareRowHasMealPlan ? ' hotel-text-container--title-with-mealplan' : ''
                  }`}
              >
                {compareRowHasMealPlan ? (
                  <div className="modal-compare__title-mealplan-row">
                    <div className="modal-compare__title-col">
                      <Tooltip title={hotelItem.displayName} placement="top">
                        <p>{hotelItem.displayName}</p>
                      </Tooltip>
                    </div>
                    <div className="modal-compare__meal-plan-col">
                      {!isEmpty(hotelItem.mealPLan?.trim()) ? (
                        <span
                          className="modal-compare__meal-plan-badge"
                          style={
                            MODAL_COMPARE_MEAL_PLAN_BADGE_COLORS[
                            getModalCompareMealPlanBadgeKind(hotelItem.mealPLan)
                            ]
                          }
                        >
                          {hotelItem.mealPLan}
                        </span>
                      ) : null}
                    </div>
                  </div>
                ) : (
                  <Tooltip title={hotelItem.displayName} placement="top">
                    <p>{hotelItem.displayName}</p>
                  </Tooltip>
                )}
              </div>

              <div className="hotel-text-container hotel-text-container--address">
                <Tooltip
                  title={`${hotelItem.address}, ${hotelItem.city}, ${hotelItem.state}, ${hotelItem.countryCode}`}
                  placement="top"
                >
                  <p>
                    {hotelItem.address}, {hotelItem.city}, {hotelItem.state}, {hotelItem.countryCode}
                  </p>
                </Tooltip>
              </div>

              {hotelItem.refundability && hotelItem.refundability !== RefundableEnum.Unknown ? (
                <div className="hotel-text-container hotel-text-container--refundable">
                  <div className="hotel-card-wrapper__refundable">
                    {hotelItem.refundability === RefundableEnum.Refundable && (
                      <div className="hotel-card-wrapper__refundable-ref">
                        <FormattedMessage id="refundable" />
                      </div>
                    )}
                    {hotelItem.refundability === RefundableEnum.Nonrefundable && (
                      <div className="hotel-card-wrapper__refundable-nonref">
                        <FormattedMessage id="non.refundable" />
                      </div>
                    )}
                  </div>
                </div>
              ) : null}

              {hotelItem.starRating >= 0 || hotelItem.rating?.value || hotelItem.rating?.reviewsCount ? (
                <div className="hotel-amenities-container">
                  {hotelItem.starRating >= 0 ? <HotelStars stars={hotelItem.starRating} /> : null}
                  <label>
                    {hotelItem.rating?.value ? (
                      <>
                        <strong>{hotelItem.rating?.value}</strong>/10
                      </>
                    ) : null}{' '}
                    {hotelItem.rating?.reviewsCount ? (
                      <>
                        ({hotelItem.rating?.reviewsCount} <FormattedMessage id="reviews" />)
                      </>
                    ) : null}
                  </label>
                </div>
              ) : null}

              <div className="hotel-text-container hotel-text-container--price">
                {publicPrice ? (
                  <p style={{ textDecoration: 'line-through' }}>
                    <Currency currency={hotelItem.currency} />
                    {renderNumber(publicPrice, 2)}
                  </p>
                ) : null}
                <p>
                  <strong>
                    <Currency currency={hotelItem.currency} />
                    {renderNumber(marginatorPrice, 2)}
                  </strong>
                </p>
              </div>

              <div className="hotel-text-container" style={{ height: '87px' }}>
                {hotelItem?.amenities?.length ? (
                  <>
                    {hotelItem?.amenities?.filter((a) => !isEmpty(a))?.length > 0 ? (
                      hotelItem
                        ?.amenities.map((a) => ({ ...(getAmenityImage(a) as Record<string, unknown>), name: a.name }))
                        .filter((a) => !!a)
                        .slice(0, 2)
                        .map((a, i) => (
                          <div key={i} className="modal-compare__amenity-row">
                            <img src={(a as { image?: string }).image || NoImage} alt={a.name || 'no-picture'} width="33" />
                            <div className="modal-compare__amenity-label-wrap">
                              <Tooltip title={a.name || ''} placement="top">
                                <span className="modal-compare__amenity-name">{a.name}</span>
                              </Tooltip>
                            </div>
                          </div>
                        ))
                    ) : (
                      <FormattedMessage id="results.hotel.card.compare.modal.no_amenities" />
                    )}
                  </>
                ) : (
                  <FormattedMessage id="results.hotel.card.compare.modal.no_amenities" />
                )}
              </div>

              <div className="hotel-view-container">
                <div className="view-button" onClick={() => handleView(hotelItem, marginatorCommission)}>
                  <FormattedMessage id="results.hotel.card.compare.modal.view" />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="modal-compare__buttons">
        {!account || account.type !== LoginType.PrivateWithToken ? (
          account?.displayShareCompareHotels && (
            <BlueButton disabled={loading} onClick={handelDownload}>
              {loading ? <Spin /> : <FormattedMessage id="results.hotel.card.compare.modal.download" />}
            </BlueButton>
          )
        ) : (
          <>
            {account?.displayShareCompareHotels && (
              <BlueButton
                disabled={loading}
                onClick={handelDownload}
              >
                {loading ? <Spin /> : <FormattedMessage id="results.hotel.card.compare.modal.download" />}
              </BlueButton>
            )}

            {HasPermission(user, ViewQuotesSharePermission) ? (
              <BlueButton
                disabled={loading}
                onClick={handelShare}
              >
                {loading ? <Spin /> : <FormattedMessage id="results.hotel.card.compare.modal.share" />}
              </BlueButton>
            ) : null}
          </>
        )}
      </div>
      <ModalConfirmation
        className="modal-share"
        wrapClassName="modal-share__confirmation-wrapper"
        title="results.hotel.card.compare.modal.share"
        message={redirectMessage}
        yesMessage="share.continue"
        noMessage="share.cancel"
        visible={showRedirectConfirmation}
        disableMessageLocalization
        onCancel={onCancelRedirect}
        onConfirm={onConfirmRedirect}
      />
    </div>
  );
};
